<template>
	<BlockVisibilityObserver title="FINAL_CTA">
		<section class="kova-lp-cta">
			<div class="kova-lp-cta__panel">
				<div class="kova-lp-cta__pane">
					<div class="kova-lp-cta__copy">
						<p class="kova-lp-cta__badge">{{ badge }}</p>
						<h2 class="kova-lp-cta__title">
							<span
								v-for="(part, index) in titleParts"
								:key="index"
								:class="{ 'kova-lp-cta__title_accent': part.accent }"
								v-text="part.text"
							/>
						</h2>
						<p class="kova-lp-cta__text">{{ text }}</p>
					</div>

					<button class="kova-lp-cta__button" type="button" @click="onCtaClick">{{ ctaLabel }}</button>
				</div>

				<!-- The photo and its backdrop ship as one composed cut-out. The base pair has one
				     per viewport — Figma crops it differently in each frame; a schema's `visual`
				     is a single picture for both. -->
				<picture aria-hidden="true" class="kova-lp-cta__visual" :class="{ 'kova-lp-cta__visual_shared': visual }">
					<source v-if="!visual" media="(min-width: 1024px)" :srcset="visualDesktop" />
					<img alt="" :src="visual ?? visualMobile" />
				</picture>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script>
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import visualDesktop from '@/assets/kova/landing/cta-visual-desktop.png'
import visualMobile from '@/assets/kova/landing/cta-visual-mobile.png'

export default {
	name: 'LandingKovaCta',

	components: { BlockVisibilityObserver },

	props: {
		badge: {
			type: String,
			required: true,
		},

		ctaLabel: {
			type: String,
			required: true,
		},

		text: {
			type: String,
			required: true,
		},

		titleParts: {
			type: Array,
			required: true,
		},

		// One composed cut-out for both breakpoints, cropped for the 386x370 mobile frame.
		// Null keeps the base pair, the photo on its blue Kova-mark backdrop.
		visual: {
			type: String,
			default: null,
		},
	},

	emits: ['progressLoaderCompleted'],

	setup() {
		return { visualDesktop, visualMobile }
	},

	methods: {
		onCtaClick() {
			this.$emit('progressLoaderCompleted', 'final_cta_eligible')
		},
	},
}
</script>

<style lang="scss" scoped>
// Mobile (Figma 4843:27198): a full-bleed blue panel, photo at the bottom.
.kova-lp-cta {
	font-family: var(--kova-lp-font);
	padding-top: var(--size-40);
	background: var(--kova-lp-page);
}

.kova-lp-cta__panel {
	position: relative;
	overflow: hidden;

	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-32);
	padding: var(--size-24) var(--size-16) 0;

	background: var(--kova-lp-cta-panel);
}

.kova-lp-cta__pane {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-32);
	width: 100%;
}

.kova-lp-cta__copy {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-16);
	width: 100%;

	text-align: center;
}

.kova-lp-cta__badge {
	margin: 0;
	padding: var(--size-8) var(--size-14);

	font-size: var(--size-14);
	font-weight: 600;
	line-height: var(--size-20);
	letter-spacing: 0.14px;

	border: 0.4px solid var(--Fill-White);
	border-radius: var(--size-20);
	color: var(--kova-lp-cta-panel-ink);
	background: var(--kova-lp-cta-badge-bg);
	backdrop-filter: blur(10px);
	text-transform: uppercase;
	white-space: nowrap;
}

.kova-lp-cta__title {
	margin: 0;

	font-size: var(--size-32);
	font-weight: 500;
	line-height: 1.12;
	letter-spacing: -0.64px;

	color: var(--kova-lp-text);
}

.kova-lp-cta__title_accent {
	color: var(--kova-lp-cta-panel-accent);
}

.kova-lp-cta__text {
	margin: 0;

	font-size: var(--size-16);
	line-height: 1.5;

	color: var(--kova-lp-cta-panel-ink);
}

.kova-lp-cta__button {
	box-sizing: border-box;
	width: 100%;
	height: var(--size-52);
	padding: var(--size-16) var(--size-32);

	font-family: inherit;
	font-size: var(--size-16);
	font-weight: 500;
	line-height: var(--size-20);

	border: none;
	border-radius: var(--size-16);
	color: var(--Fill-White);
	background: var(--kova-lp-brand);
	cursor: pointer;
}

.kova-lp-cta__visual {
	display: block;
	width: 386px;
	max-width: 100%;

	img {
		display: block;
		width: 100%;
		height: auto;
	}
}

// The cut-out a schema hands over is cropped to the mobile frame's 386 of the 390 panel
// (6350:48687): it runs out through the panel's 16px padding to 2px from either edge, so its
// crop lines land at the panel edges instead of 16px inside them.
@media (max-width: 1023px) {
	.kova-lp-cta__visual_shared {
		width: calc(100% + var(--size-28));
		max-width: none;
		margin-inline: calc(-1 * var(--size-14));
	}
}

// Desktop (Figma 4841:21481): rounded card, copy left, photo right.
//
// The 1200px panel is the frame's content width. At >=1440 the auto margins
// reproduce Figma's 120px gutters exactly; below that the gutter collapses to 40
// and the panel reflows instead of overflowing.
@media (min-width: 1024px) {
	.kova-lp-cta {
		padding: var(--size-64) var(--size-40) var(--size-80);
	}

	.kova-lp-cta__panel {
		box-sizing: border-box;
		max-width: 1200px;
		margin-inline: auto;
		padding: 0;

		// 636 / 564 are the frame's two columns, kept as a ratio so the pair keeps
		// its proportion at any panel width. The 564px-square photo drives the
		// panel height, which is why no explicit height is needed.
		display: grid;
		grid-template-columns: minmax(0, 636fr) minmax(0, 564fr);
		align-items: stretch;
		gap: 0;

		border-radius: var(--size-36);
	}

	.kova-lp-cta__pane {
		box-sizing: border-box;
		align-items: start;
		gap: var(--size-40);
		padding: var(--size-48) var(--size-8) var(--size-48) var(--size-48);
	}

	.kova-lp-cta__copy {
		align-items: start;
		gap: var(--size-20);

		text-align: left;
	}

	.kova-lp-cta__badge {
		font-size: var(--size-16);
		line-height: normal;
		letter-spacing: 0.16px;
	}

	.kova-lp-cta__title {
		font-size: var(--size-48);
		line-height: 1.2;
		letter-spacing: -0.96px;
	}

	.kova-lp-cta__text {
		font-size: var(--size-20);
		line-height: 1.4;
	}

	.kova-lp-cta__button {
		margin-top: auto; // pinned to the bottom of the pane, as in 4841:21490

		// Figma states 368x90 with 20/116 padding around a hugged label; a button
		// centres its own text, so only the size cap has to be said here.
		width: 100%;
		max-width: 368px;
		height: 90px; // 4841:21490 — between --size-88 and --size-96, kept exact
		padding: 0 var(--size-24);

		font-size: var(--size-24);
		line-height: normal;

		border-radius: var(--size-28);
	}

	.kova-lp-cta__visual {
		align-self: center;
		width: 100%;
	}

	// A shared picture is the mobile crop — 386x370, a little short of the square column.
	// Pinned to the bottom, the photo is still cut by the panel edge as in both frames
	// (6350:48687 / 6350:48721); the shortfall goes to the top.
	.kova-lp-cta__visual_shared {
		align-self: end;
	}
}
</style>
